<script>
  import { createEventDispatcher, onMount } from "svelte";
  import alertify from "alertifyjs";
  import "alertifyjs/build/css/alertify.css";
  import moment from "moment";
  import { normalizeCampaignRow, getStatusInfo } from "../../../../../utils/freebetsCore.js";
  import { computeCampaignStatus } from "../../../../../utils/statusEngine.js";
  import { getClientFilterCodes } from "../../../../../../js/credentialsApi.js";

  moment.locale("es");

  export let provider = "pg";
  export let benefitType = "free-spins";
  export let api = {
    listByProvider: async () => { throw new Error("api.listByProvider no inyectado"); },
    cancelCampaign: async () => { throw new Error("api.cancelCampaign no inyectado"); },
    getById: async () => null,
  };
  export let clients = [];
  export let filters = {};
  export let icons = {};
  export let allowCancel = true;
  export let timeFormat = "DD/MM, hh:mmA";

  const dispatch = createEventDispatcher();
  const PAGE_SIZE_OPTIONS = [10, 50, 100, 200];

  let rows = [];
  let filteredRows = [];
  let page = 1;
  let pageSize = 10;
  let isLoading = false;
  let errorMessage = "";
  let loadSequence = 0;
  let mounted = false;
  let lastFetchToken = "";
  let sortKey = "#";
  let sortAsc = false;

  const normalized = (value) => String(value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();

  function findClient(client = {}) {
    const clientCode = normalized(client?.clientCode ?? client?.code);
    const clientId = normalized(client?.clientId ?? client?.id);
    return (Array.isArray(clients) ? clients : []).find((item) =>
      (clientCode && normalized(item?.code ?? item?.clientCode) === clientCode) ||
      (clientId && normalized(item?.id ?? item?.clientId) === clientId),
    );
  }

  function enrichClient(client = {}) {
    const catalogClient = findClient(client);
    return {
      ...client,
      clientName:
        catalogClient?.namesite ??
        catalogClient?.clientName ??
        catalogClient?.name ??
        client?.clientName ??
        client?.clientCode ??
        "—",
    };
  }

  function enrichGame(value) {
    const gameId = String(value?.gameID ?? value?.gameId ?? value?.id ?? value ?? "").trim();
    return {
      ...(value && typeof value === "object" ? value : {}),
      gameId,
      gameName: value?.gameName ?? value?.name ?? gameId ?? "—",
    };
  }

  function enrichCampaign(document = {}) {
    return {
      ...document,
      client: enrichClient(document?.client),
      slotConfigs: Array.isArray(document?.slotConfigs)
        ? document.slotConfigs.map((config) => ({ ...config, game: enrichGame(config?.game) }))
        : document?.slotConfigs,
      slotConfig: document?.slotConfig
        ? {
            ...document.slotConfig,
            games: Array.isArray(document.slotConfig.games)
              ? document.slotConfig.games.map(enrichGame)
              : document.slotConfig.games,
          }
        : document?.slotConfig,
      liveConfig: document?.liveConfig
        ? {
            ...document.liveConfig,
            games: Array.isArray(document.liveConfig.games)
              ? document.liveConfig.games.map(enrichGame)
              : document.liveConfig.games,
          }
        : document?.liveConfig,
    };
  }

  function detectedBenefitType(document = {}) {
    const nameAndId = normalized(`${document?.campaignName ?? ""} ${document?.campaignId ?? ""}`);
    if (nameAndId.includes("free chip") || nameAndId.includes("freechip")) return "free-chips";
    const raw = normalized(
      document?.benefitType ??
      document?.bonusType ??
      document?.campaignBonusType ??
      document?.rewardType ??
      document?.promotionType,
    ).replace(/[\s_]+/g, "-");
    if (raw.includes("chip")) return "free-chips";
    if (raw.includes("spin") || raw.includes("round")) return "free-spins";
    if (document?.freeChipConfig || document?.chipConfig) return "free-chips";
    return "free-spins";
  }

  function mapCampaign(document) {
    const enriched = enrichCampaign(document);
    const base = normalizeCampaignRow(enriched);
    const startMs = base.startDate ? new Date(base.startDate).getTime() : (base.startTime || 0);
    const endMs = base.endDate ? new Date(base.endDate).getTime() : 0;
    const effectiveStatus = computeCampaignStatus(base.statusCode, startMs, endMs);
    const statusInfo = getStatusInfo(effectiveStatus);
    return {
      ...base,
      benefitType: detectedBenefitType(enriched),
      statusCode: effectiveStatus,
      statusInfoClass: statusInfo.class,
      statusInfoLabel: statusInfo.label,
      _configExpanded: false,
    };
  }

  async function sessionClientParams() {
    const allowedCodes = await getClientFilterCodes();
    if (allowedCodes === null || !allowedCodes.length) return {};
    return { clientCodes: allowedCodes.join(",") };
  }

  export async function refresh() {
    return load();
  }

  export async function reconcileOne(campaignId) {
    if (!api?.getById) return null;
    const response = await api.getById(campaignId);
    const raw = response?.data ?? response;
    const document = Array.isArray(raw) ? raw[0] : raw;
    if (!document) return null;
    const updated = mapCampaign(document);
    rows = rows.map((row) =>
      String(row.campaignId) === String(campaignId) ? updated : row,
    );
    return updated;
  }

  async function load() {
    if (!provider || typeof api?.listByProvider !== "function") {
      rows = [];
      return;
    }
    const sequence = ++loadSequence;
    isLoading = true;
    errorMessage = "";
    try {
      const params = { provider, ...(await sessionClientParams()) };
      const response = await api.listByProvider(provider, params);
      if (sequence !== loadSequence) return;
      const raw = response?.data?.list ?? response?.data ?? response ?? [];
      rows = (Array.isArray(raw) ? raw : []).map(mapCampaign);
      dispatch("loaded", { campaigns: rows, benefitType });
    } catch (error) {
      if (sequence !== loadSequence) return;
      console.error("Error cargando campañas Pragmatic:", error);
      rows = [];
      errorMessage = "No se pudo cargar la lista de campañas Pragmatic.";
    } finally {
      if (sequence === loadSequence) isLoading = false;
    }
  }

  function includes(value, search) {
    return !search || normalized(value).includes(normalized(search));
  }

  function statusMatches(row) {
    const statuses = Array.isArray(filters?.statuses) ? filters.statuses : [];
    return !statuses.length || statuses.map(Number).includes(Number(row.statusCode));
  }

  function dateMatches(row) {
    const from = filters?.dateFrom ? new Date(filters.dateFrom).getTime() : null;
    const to = filters?.dateTo ? new Date(filters.dateTo).getTime() : null;
    const start = row.startDate ? new Date(row.startDate).getTime() : (row.startTime || 0);
    const end = row.endDate ? new Date(row.endDate).getTime() : Number.MAX_SAFE_INTEGER;
    if (from !== null && end < from) return false;
    if (to !== null && start > to) return false;
    return true;
  }

  function playerMatches(row) {
    const player = normalized(filters?.player);
    const bonus = normalized(filters?.bonus);
    const playerList = Array.isArray(row.playerList) ? row.playerList : [];
    const playerOk = !player || playerList.some((item) =>
      includes(item?.playerId, player) || includes(item?.playerName, player),
    );
    const bonusOk = !bonus || playerList.some((item) => includes(item?.bonusCode, bonus));
    return playerOk && bonusOk;
  }

  function matchesFilters(row) {
    const expectedCampaignType = benefitType === "free-chips" ? "live" : "slot";
    const clientOk = !filters?.client ||
      includes(row.clientLabel, filters.client) ||
      includes(row?.client?.clientCode, filters.client) ||
      String(row?.client?.clientId ?? "") === String(filters?.clientId ?? "");
    return row.benefitType === benefitType &&
      normalized(row.campaignType) === expectedCampaignType &&
      clientOk &&
      includes(row.campaignName, filters?.campaignName) &&
      includes(row.gameLabel, filters?.game) &&
      (!filters?.currency || normalized(row.currencyLabel) === normalized(filters.currency)) &&
      statusMatches(row) &&
      dateMatches(row) &&
      playerMatches(row);
  }

  function sortValue(row, key) {
    switch (key) {
      case "client": return normalized(row.clientLabel);
      case "campaign": return normalized(row.campaignName);
      case "type": return normalized(row.campaignType);
      case "game": return normalized(row.gameLabel);
      case "currency": return normalized(row.currencyLabel);
      case "start": return row.startDate ? new Date(row.startDate).getTime() : 0;
      case "end": return row.endDate ? new Date(row.endDate).getTime() : 0;
      case "status": return Number(row.statusCode);
      default: return 0;
    }
  }

  function setSort(key, preferredAsc = null) {
    if (sortKey === key) sortAsc = !sortAsc;
    else {
      sortKey = key;
      sortAsc = preferredAsc ?? true;
    }
  }

  function sorted(list) {
    const copy = [...list];
    if (sortKey === "#") return sortAsc ? copy : copy.reverse();
    return copy.sort((left, right) => {
      const a = sortValue(left, sortKey);
      const b = sortValue(right, sortKey);
      const comparison = typeof a === "string" ? a.localeCompare(b) : Number(a) - Number(b);
      return sortAsc ? comparison : -comparison;
    });
  }

  async function cancelCampaign(campaign) {
    if (!allowCancel || !confirm(`¿Seguro que deseas eliminar la campaña ${campaign.campaignName}?`)) return;
    try {
      await api.cancelCampaign(campaign.campaignId, campaign);
      await load();
      alertify.success("Campaña eliminada exitosamente");
      dispatch("cancelled", { campaignId: campaign.campaignId });
    } catch (error) {
      console.error("Error cancelando campaña Pragmatic:", error);
      alert("No se pudo eliminar la campaña.");
    }
  }

  function toggleConfig(campaign) {
    rows = rows.map((row) =>
      row.campaignId === campaign.campaignId
        ? { ...row, _configExpanded: !row._configExpanded }
        : row,
    );
  }

  const getRoundLabel = (value) => {
    return value == '0'? 'Instantaneas': value;
  }

  $: filterToken = JSON.stringify(filters || {});
  $: {
    filterToken;
    filteredRows = sorted(rows.filter(matchesFilters));
  }
  $: totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  $: if (page > totalPages) page = totalPages;
  $: pageStart = (page - 1) * pageSize;
  $: visibleRows = filteredRows.slice(pageStart, pageStart + pageSize);
  $: if (mounted && filterToken) page = 1;
  $: fetchToken = JSON.stringify({ provider, benefitType });
  $: if (mounted && fetchToken !== lastFetchToken) {
    lastFetchToken = fetchToken;
    load();
  }

  onMount(() => {
    mounted = true;
    lastFetchToken = fetchToken;
    load();
    const handler = () => refresh();
    window.addEventListener("campaign:created", handler);
    return () => window.removeEventListener("campaign:created", handler);
  });
</script>

{#if isLoading}
  <p class="state-message">Cargando campañas Pragmatic…</p>
{:else if errorMessage}
  <p class="state-message error">{errorMessage}</p>
{:else if visibleRows.length === 0}
  <p class="state-message">
    No se encontraron {benefitType === "free-chips" ? "Free Chips" : "Free Spins"}.
  </p>
{:else}
  <div class="table-wrapper">
    <table class="table table-striped table-hover table-bordered align-middle">
      <thead>
        <tr>
          <th class="th-sort" on:click={() => setSort("#")}>N°</th>
          <th class="th-sort" on:click={() => setSort("client", true)}>Cliente</th>
          <th class="th-sort" on:click={() => setSort("campaign", true)}>Campaña</th>
          <th class="th-sort" on:click={() => setSort("type", true)}>Tipo</th>
          <th class="th-sort" on:click={() => setSort("game", true)}>
            {benefitType === "free-chips" ? "Juegos / Detalles" : "Juegos / Monto / Rondas"}
          </th>
          <th class="th-sort" on:click={() => setSort("currency", true)}>Moneda</th>
          <th class="th-sort" on:click={() => setSort("start", false)}>Inicio</th>
          <th class="th-sort" on:click={() => setSort("end", false)}>Fin</th>
          <th class="th-sort" on:click={() => setSort("status", false)}>Estado</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {#each visibleRows as campaign, index (campaign.campaignId)}
          <tr>
            <td>{pageStart + index + 1}</td>
            <td>{campaign.clientLabel}</td>
            <td>{campaign.campaignName || "—"}</td>
            <td>{campaign.campaignType}</td>
            <td>
              {#if benefitType === "free-chips"}
                <div class="free-chip-config">
                  <span class="free-chip-games" title={campaign.gameLabel}>{campaign.gameLabel || "—"}</span>
                  <span class="free-chip-meta">
                    Fichas: {campaign?.liveConfig?.chipNumber ?? "—"}
                    Valor ficha: {campaign?.liveConfig?.chipValue ?? "—"}
                    Máx. ganancia: {campaign?.liveConfig?.maxWin ?? "—"}
                  </span>
                </div>
              {:else if campaign.gameConfigs?.length}
                {@const limit = 2}
                {@const total = campaign.gameConfigs.length}
                {@const showAll = campaign._configExpanded || total <= limit}
                <div class="config-table">
                  {#each campaign.gameConfigs as config, configIndex}
                    {#if showAll || configIndex < limit}
                      <div class="config-card">
                        <span class="config-game" title={config.game}>{config.game}</span>
                        <span class="config-meta">Monto: {config.amount} · Rondas: {getRoundLabel(config.rounds)}</span>
                      </div>
                    {/if}
                  {/each}
                </div>
                {#if total > limit}
                  <button class="btn btn-link btn-sm config-toggle" on:click|stopPropagation={() => toggleConfig(campaign)}>
                    {showAll ? "Ver menos" : `Ver más (${total - limit})`}
                  </button>
                {/if}
              {:else}
                <span class="text-muted">—</span>
              {/if}
            </td>
            <td>{campaign.currencyLabel}</td>
            <td title={campaign.startDate ? moment(campaign.startDate).local().format("dddd D [de] MMMM YYYY, hh:mm A") : ""}>
              <span class="date-cell">{campaign.startDate ? moment(campaign.startDate).local().format(timeFormat) : "—"}</span>
            </td>
            <td title={campaign.endDate ? moment(campaign.endDate).local().format("dddd D [de] MMMM YYYY, hh:mm A") : ""}>
              <span class="date-cell">{campaign.endDate ? moment(campaign.endDate).local().format(timeFormat) : "—"}</span>
            </td>
            <td>
              <span class={`badge status-badge rounded-pill ${campaign.statusInfoClass}`}>
                <span class="status-dot"></span>{campaign.statusInfoLabel}
              </span>
            </td>
            <td>
              <div class="actions-content">
                <button class="btn btn-icon btn-view" title="Ver jugadores" on:click={() => dispatch("openplayers", campaign)}>
                  {@html icons.persons || "+"}
                </button>
                {#if allowCancel}
                  <button
                    class="btn btn-icon btn-delete"
                    disabled={[0, 3, 5].includes(Number(campaign.statusCode))}
                    title="Eliminar campaña"
                    on:click={() => cancelCampaign(campaign)}
                  >
                    {@html icons.delete || "×"}
                  </button>
                {/if}
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<div class="pagination-bar">
  <span class="total-badge">Total: <strong>{filteredRows.length}</strong></span>
  <div class="page-controls">
    <button on:click={() => (page = 1)} disabled={page === 1} aria-label="Primera página">«</button>
    <button on:click={() => (page = Math.max(1, page - 1))} disabled={page === 1} aria-label="Página anterior">‹</button>
    <span>Página <strong>{page}</strong> de <strong>{totalPages}</strong></span>
    <button on:click={() => (page = Math.min(totalPages, page + 1))} disabled={page >= totalPages} aria-label="Página siguiente">›</button>
    <button on:click={() => (page = totalPages)} disabled={page >= totalPages} aria-label="Última página">»</button>
  </div>
  <select bind:value={pageSize} on:change={() => { pageSize = Number(pageSize); page = 1; }}>
    {#each PAGE_SIZE_OPTIONS as option}<option value={option}>{option} / pág.</option>{/each}
  </select>
</div>

<style>
  .state-message { margin: 1rem; color: #6c757d; text-align: center; }
  .state-message.error { color: #dc3545; }
  .table-wrapper {
    max-height: 70vh;
    margin: 0 10px;
    overflow: auto;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.5rem;
  }
  .table-wrapper table { margin-bottom: 0; }
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 0.6rem 0.5rem;
    border-bottom: 0;
    background: #fff;
    box-shadow: 0 1px 0 #dee2e6;
    color: #555;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  tbody td { padding: 0.35rem 0.5rem; vertical-align: middle; font-size: 0.85rem; }
  .th-sort { cursor: pointer; user-select: none; white-space: nowrap; }
  .date-cell { white-space: nowrap; }
  .config-table { display: flex; min-width: 220px; flex-direction: column; gap: 0.35rem; }
  .config-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    padding: 0.25rem 0.45rem;
    border: 1px solid #e9ecef;
    border-radius: 0.4rem;
    background: #fff;
    font-size: 0.78rem;
  }
  .config-game { max-width: 140px; overflow: hidden; color: #0d6efd; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
  .config-meta { color: #555; white-space: nowrap; }
  .free-chip-config { display: flex; min-width: 240px; flex-direction: column; gap: 0.3rem; }
  .free-chip-games { max-width: 320px; overflow: hidden; color: #0d6efd; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
  .free-chip-meta { color: #555; font-size: 0.78rem; white-space: nowrap; }
  .config-toggle { padding: 0; color: #ff0090; font-size: 0.75rem; text-decoration: none; }
  .status-badge { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.35em 0.75em; font-size: 0.75rem; font-weight: 600; }
  .status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  .actions-content { display: flex; align-items: center; justify-content: center; gap: 0.35rem; }
  .btn-icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; padding: 0; border: 1px solid transparent; border-radius: 8px; }
  .btn-view { border-color: #cfe2ff; background: #f0f7ff; color: #0d6efd; }
  .btn-delete { border-color: #f1aeb5; background: #fff5f5; color: #dc3545; }
  .btn-view:hover { border-color: #0d6efd; background: #0d6efd; color: #fff; }
  .btn-delete:hover:not(:disabled) { border-color: #dc3545; background: #dc3545; color: #fff; }
  .btn-icon:disabled { cursor: not-allowed; opacity: 0.45; }
  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0.5rem 10px 0;
    padding: 0.65rem 0.75rem;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.5rem;
    background: #fff;
  }
  .total-badge, .page-controls, .pagination-bar select { color: #555; font-size: 0.8rem; }
  .page-controls { display: flex; align-items: center; gap: 0.35rem; }
  .page-controls button { width: 34px; height: 34px; padding: 0; border: 1px solid #e5e7eb; border-radius: 0.4rem; background: #fff; }
  .page-controls button:disabled { cursor: not-allowed; opacity: 0.45; }
  .pagination-bar select { padding: 0.3rem 0.55rem; border: 1px solid #e5e7eb; border-radius: 0.4rem; background: #fff; }
  @media (max-width: 720px) { .pagination-bar { flex-direction: column; } }
</style>
